import { Info } from 'lucide-react'
import { memo, type FC, type ReactNode } from 'react'

import type { DynamicField } from '../types'
import type { Any } from '@api/orvalFetcher'
import { Popover, PopoverTrigger, PopoverContent, Label } from '@fp/ui-kit'

export type FormField = { value: Any; onChange: (value: string | boolean | number | Any) => void }

export type FieldRendererProps = {
  paramsField: DynamicField
  field: FormField
  error?: string
  helperText?: string
}

export const FieldHelpPopover: FC<{ fieldName?: string; helperText?: string }> = memo(({ fieldName, helperText }) =>
  helperText ? (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type='button'
          aria-label={`${fieldName || 'Field'} info`}
          className='inline-flex size-5 items-center justify-center rounded-full text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring'
        >
          <Info className='size-4' />
        </button>
      </PopoverTrigger>
      <PopoverContent side='top' sideOffset={2} showArrow className='max-w-80 break-words text-sm leading-relaxed'>
        {helperText}
      </PopoverContent>
    </Popover>
  ) : null,
)

export const FieldShell: FC<Omit<FieldRendererProps, 'field'> & { children: ReactNode }> = memo(
  ({ paramsField, error, helperText, children }) => (
    <div className='grid gap-1'>
      <div className='flex items-center gap-1'>
        <Label>
          {paramsField.name}
          {paramsField.required && <span className='text-destructive-foreground'> *</span>}
        </Label>
        <FieldHelpPopover fieldName={paramsField.name} helperText={helperText} />
      </div>
      {children}
      <p className={`text-xs text-destructive-foreground ${error ? 'opacity-100' : 'opacity-0'}`}>{error}</p>
    </div>
  ),
)
